import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FaPlus, FaTimes, FaSave, FaCamera, FaCheckCircle,
  FaExclamationTriangle, FaFileInvoiceDollar, FaImage, FaTrash,
  FaBoxOpen, FaTruck, FaEllipsisV
} from 'react-icons/fa';
import './css_pages/Invoices.css';
import {
  getOrderDatesForInvoice,
  getOrdersByDateForInvoice,
  getInvoiceByOrder,
  getInvoiceById,
  saveInvoice,
  getAllInvoices,
  getProductsForStore,
  uploadReceiptImages,
  removeReceiptImage,
  getVendorsForStore,
  deleteInvoice
} from '../services/invoicesService';
import { useStore } from '../contexts/StoreContext';

// ── Helpers ──────────────────────────────────────────────────
const formatDate = (d) => {
  if (!d) return '—';
  const str = d instanceof Date ? d.toISOString().split('T')[0] : String(d).split('T')[0];
  const [y, m, day] = str.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[parseInt(m)-1]} ${parseInt(day)}, ${y}`;
};

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

const formatCurrency = (v) =>
  v !== null && v !== undefined ? `$${parseFloat(v).toFixed(2)}` : '—';

// Interpreta received_qty / price_invoice vacíos como 0 en vez de null,
// para que lo que el usuario ve en el placeholder sea lo que realmente se guarda.
const parseQtyOrZero = (val) =>
  val !== '' && val !== null && val !== undefined ? parseFloat(val) : 0;

// receipt_url / receipt_public_id pueden venir en 3 formatos:
//  - null / vacío            → sin fotos
//  - JSON array stringified  → ["url1","url2",...] (formato nuevo, múltiples fotos)
//  - string plano             → una sola URL (formato legacy, invoices viejos)
const parseReceiptImages = (rawUrl, rawPublicId) => {
  let urls = [];
  let ids = [];

  if (rawUrl) {
    try {
      const parsed = JSON.parse(rawUrl);
      urls = Array.isArray(parsed) ? parsed : [rawUrl];
    } catch {
      urls = [rawUrl]; // legacy: era una URL plana, no JSON
    }
  }

  if (rawPublicId) {
    try {
      const parsed = JSON.parse(rawPublicId);
      ids = Array.isArray(parsed) ? parsed : [rawPublicId];
    } catch {
      ids = [rawPublicId];
    }
  }

  return urls.map((url, i) => ({ url, public_id: ids[i] || null }));
};

// ─────────────────────────────────────────────────────────────
function Invoices() {
  const { selectedStore } = useStore();
  const fileInputRef = useRef(null);

  // ── Vista ──
  const [view, setView] = useState('list');

  // ── Selectores ──
  const [availableDates, setAvailableDates]   = useState([]);
  const [selectedDate, setSelectedDate]       = useState('');
  const [ordersForDate, setOrdersForDate]     = useState([]);
  const [loadingDates, setLoadingDates]       = useState(false);
  const [loadingOrders, setLoadingOrders]     = useState(false);

  // ── Lista invoices ──
  const [invoicesList, setInvoicesList]       = useState([]);
  const [loadingList, setLoadingList]         = useState(false);
  const [sortBy, setSortBy]                   = useState('date_desc');
  const [error, setError]                     = useState(null);

  // ── Formulario ──
  const [currentInvoice, setCurrentInvoice]   = useState(null);
  const [items, setItems]                     = useState([]);
  const [invoiceNumber, setInvoiceNumber]     = useState('');
  const [notes, setNotes]                     = useState('');
  const [saving, setSaving]                   = useState(false);
  const [savedDraft, setSavedDraft]           = useState(false);
  const [loadingInvoice, setLoadingInvoice]   = useState(false);

  // ── Fecha editable ──
  const [invoiceDate, setInvoiceDate]         = useState(todayISO());

  // ── Fotos del recibo (múltiples) ──
  // receiptImages: [{ url, public_id }]
  const [receiptImages, setReceiptImages]       = useState([]);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [removingImageIndex, setRemovingImageIndex] = useState(null);

  // ── Add Product ──
  const [showProductModal, setShowProductModal] = useState(false);
  const [allProducts, setAllProducts]           = useState([]);
  const [productSearch, setProductSearch]       = useState('');
  const [loadingProducts, setLoadingProducts]   = useState(false);
  const [productFormValues, setProductFormValues] = useState({});

  // ── Resultado save ──
  const [showResultModal, setShowResultModal]   = useState(false);
  const [saveResult, setSaveResult]             = useState(null);

  // ── Delete ──
  const [deletingId, setDeletingId]             = useState(null);
  const [showDeleteModal, setShowDeleteModal]   = useState(false);
  const [invoiceToDelete, setInvoiceToDelete]   = useState(null);

  // ── NO ORDER MODE ──
  const [noOrderMode, setNoOrderMode]           = useState(false);
  const [vendors, setVendors]                   = useState([]);
  const [selectedVendor, setSelectedVendor]     = useState(null);
  const [showVendorModal, setShowVendorModal]   = useState(false);
  const [vendorSearch, setVendorSearch]         = useState('');
  const [loadingVendors, setLoadingVendors]     = useState(false);

  // ── Mobile overflow menu ──
  const [showMobileMenu, setShowMobileMenu]     = useState(false);
  const mobileMenuRef = useRef(null);

  // Close mobile menu on outside click
  useEffect(() => {
    if (!showMobileMenu) return;
    const handler = (e) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)) {
        setShowMobileMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMobileMenu]);

  // ============================================================
  // FETCH
  // ============================================================
  const fetchDates = useCallback(async () => {
    if (!selectedStore) return;
    try {
      setLoadingDates(true);
      setSelectedDate(''); setOrdersForDate([]);
      const res = await getOrderDatesForInvoice(selectedStore.id);
      if (res.success) setAvailableDates(res.data);
    } catch { setError('Error loading dates'); }
    finally  { setLoadingDates(false); }
  }, [selectedStore]);

  const fetchInvoices = useCallback(async () => {
    if (!selectedStore) return;
    try {
      setLoadingList(true);
      const res = await getAllInvoices(selectedStore.id);
      if (res.success) setInvoicesList(res.data);
    } catch { setError('Error loading invoices'); }
    finally  { setLoadingList(false); }
  }, [selectedStore]);

  useEffect(() => { fetchDates(); fetchInvoices(); }, [fetchDates, fetchInvoices]);

  const handleDateChange = async (date) => {
    setSelectedDate(date); setOrdersForDate([]);
    if (!date) return;
    try {
      setLoadingOrders(true);
      const res = await getOrdersByDateForInvoice(selectedStore.id, date);
      if (res.success) setOrdersForDate(res.data);
    } catch { setError('Error loading orders'); }
    finally  { setLoadingOrders(false); }
  };

  // ============================================================
  // ABRIR INVOICE (from order)
  // ============================================================
  const openInvoice = async (orderId, order) => {
    try {
      setLoadingInvoice(true);
      const res = await getInvoiceByOrder(orderId, selectedStore.id);
      if (res.success) {
        setNoOrderMode(false);
        setSelectedVendor(null);
        setCurrentInvoice({ ...res.data, order });
        setItems(res.data.items.map(i => ({ ...i })));
        setInvoiceNumber(res.data.invoice_number || '');
        setNotes(res.data.notes || '');
        setReceiptImages(parseReceiptImages(res.data.receipt_url, res.data.receipt_public_id));
        setInvoiceDate(
          res.data.invoice_date
            ? String(res.data.invoice_date).split('T')[0]
            : todayISO()
        );
        setView('form');
      }
    } catch { alert('Error loading invoice'); }
    finally  { setLoadingInvoice(false); }
  };

  const handleOpenOrder = (order) => openInvoice(order.id_orders, order);

  const handleOpenExisting = async (inv) => {
    if (inv.no_order) {
      try {
        setLoadingInvoice(true);
        const res = await getInvoiceById(inv.id_invoice, selectedStore.id);
        if (res.success) {
          setNoOrderMode(true);
          setSelectedVendor({ id_vendors: inv.id_vendor, vendor_name: inv.vendor_name });
          setCurrentInvoice({
            id_invoice:     inv.id_invoice,
            id_order:       null,
            id_store:       selectedStore.id,
            invoice_number: inv.invoice_number,
            status:         inv.status,
            total_amount:   inv.total_amount,
            receipt_url:    res.data.receipt_url || null,
            order: {
              order_number: null,
              vendor_name:  inv.vendor_name,
              sent_at:      null
            }
          });
          setItems(res.data.items.map(i => ({ ...i })));
          setInvoiceNumber(inv.invoice_number || '');
          setNotes(inv.notes || '');
          setReceiptImages(parseReceiptImages(res.data.receipt_url, res.data.receipt_public_id));
          setInvoiceDate(
            res.data.invoice_date
              ? String(res.data.invoice_date).split('T')[0]
              : inv.created_at
                ? String(inv.created_at).split('T')[0]
                : todayISO()
          );
          setView('form');
        }
      } catch { alert('Error loading invoice'); }
      finally  { setLoadingInvoice(false); }
    } else {
      openInvoice(inv.id_orders, inv);
    }
  };

  // ============================================================
  // DELETE INVOICE
  // ============================================================
  const handleDeleteClick = (inv, e) => {
    e.stopPropagation();
    setInvoiceToDelete(inv);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!invoiceToDelete) return;
    try {
      setDeletingId(invoiceToDelete.id_invoice);
      const res = await deleteInvoice(invoiceToDelete.id_invoice, selectedStore.id);
      if (res.success) {
        setInvoicesList(prev => prev.filter(i => i.id_invoice !== invoiceToDelete.id_invoice));
        if (selectedDate) handleDateChange(selectedDate);
      }
    } catch (err) {
      alert('Error deleting invoice: ' + (err.response?.data?.message || err.message));
    } finally {
      setDeletingId(null);
      setShowDeleteModal(false);
      setInvoiceToDelete(null);
    }
  };

  // ============================================================
  // NO ORDER FLOW
  // ============================================================
  const handleOpenNoOrderFlow = async () => {
    try {
      setLoadingVendors(true);
      setVendorSearch('');
      const res = await getVendorsForStore(selectedStore.id);
      if (res.success) setVendors(res.data);
      setShowVendorModal(true);
    } catch { alert('Error loading vendors'); }
    finally  { setLoadingVendors(false); }
  };

  const handleSelectVendor = (vendor) => {
    setSelectedVendor(vendor);
    setShowVendorModal(false);
    setNoOrderMode(true);
    setCurrentInvoice({
      id_invoice:     null,
      id_order:       null,
      id_store:       selectedStore.id,
      invoice_number: null,
      status:         'Draft',
      total_amount:   0,
      order: {
        order_number: null,
        vendor_name:  vendor.vendor_name,
        sent_at:      null
      }
    });
    setItems([]);
    setInvoiceNumber('');
    setNotes('');
    setReceiptImages([]);
    setInvoiceDate(todayISO());
    setView('form');
  };

  const filteredVendors = vendors.filter(v =>
    v.vendor_name.toLowerCase().includes(vendorSearch.toLowerCase())
  );

  // ============================================================
  // FOTOS DEL RECIBO (múltiples)
  // ============================================================
  const handleCameraClick = () => {
    setShowMobileMenu(false);
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    e.target.value = '';
    try {
      setUploadingReceipt(true);
      const invoiceId = currentInvoice?.id_invoice || null;
      const orderId   = currentInvoice?.id_order   || currentInvoice?.order?.id_orders || null;
      const res = await uploadReceiptImages(files, invoiceId, orderId, selectedStore.id);
      if (res.success) {
        const newImages = res.data.images
          || (res.data.url ? [{ url: res.data.url, public_id: res.data.public_id }] : []);
        setReceiptImages(prev => [...prev, ...newImages]);
      }
    } catch (err) {
      alert('Error uploading receipt: ' + (err.response?.data?.message || err.message));
    } finally {
      setUploadingReceipt(false);
    }
  };

  const handleRemoveReceiptImage = async (idx) => {
    const img = receiptImages[idx];
    if (!img) return;
    try {
      setRemovingImageIndex(idx);
      // Si la foto ya tiene public_id, hay que borrarla de Cloudinary
      // (y de la BD si el invoice ya existe) antes de quitarla del estado.
      if (img.public_id) {
        await removeReceiptImage(currentInvoice?.id_invoice || null, img.public_id, selectedStore.id);
      }
      setReceiptImages(prev => prev.filter((_, i) => i !== idx));
    } catch (err) {
      alert('Error removing photo: ' + (err.response?.data?.message || err.message));
    } finally {
      setRemovingImageIndex(null);
    }
  };

  // ============================================================
  // EDITAR ITEMS
  // ============================================================
  const handleItemChange = (index, field, value) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    setItems(updated);
  };

  // ============================================================
  // ADD PRODUCT
  // ============================================================
  const handleOpenProductModal = async () => {
    setShowMobileMenu(false);
    try {
      setLoadingProducts(true);
      setProductSearch('');
      setProductFormValues({});
      const res = await getProductsForStore(selectedStore.id);
      if (res.success) setAllProducts(res.data);
      setShowProductModal(true);
    } catch { alert('Error loading products'); }
    finally  { setLoadingProducts(false); }
  };

  const handleProductFormChange = (productId, field, value) => {
    setProductFormValues(prev => ({
      ...prev,
      [productId]: { ...(prev[productId] || {}), [field]: value }
    }));
  };

  const handleAddProduct = (product) => {
    const formVals = productFormValues[product.id_products] || {};
    setItems(prev => [...prev, {
      id_invoice_item: null,
      id_product:      product.id_products,
      product_name:    product.product_name,
      product_code:    product.product_code,
      container_size:  product.container_size,
      container_unit:  product.container_unit,
      container_type:  product.container_type,
      case_size:       product.case_size || null,
      ordered_qty:     0,
      received_qty:    formVals.received_qty !== undefined && formVals.received_qty !== ''
                         ? formVals.received_qty : '',
      price_app:       parseFloat(product.wholesale_price) || 0,
      price_invoice:   formVals.price_invoice !== undefined && formVals.price_invoice !== ''
                         ? formVals.price_invoice : '',
      received_by:     formVals.received_by || null,
      is_extra:        1
    }]);
    setProductFormValues(prev => {
      const next = { ...prev };
      delete next[product.id_products];
      return next;
    });
    // Ya NO cerramos el modal aquí — el usuario lo cierra manualmente
    // (con la X o tocando fuera) cuando termine de agregar productos.
  };

  const handleRemoveExtra = (index) => setItems(prev => prev.filter((_, i) => i !== index));

  // ============================================================
  // SAVE DRAFT
  // ============================================================
  const handleSaveDraft = async () => {
    if (!currentInvoice) return;
    if (noOrderMode && !selectedVendor) { alert('Please select a vendor first.'); return; }
    try {
      setSaving(true);
      const payload = {
        orderId:         currentInvoice.id_order || null,
        storeId:         selectedStore.id,
        vendorId:        noOrderMode ? selectedVendor?.id_vendors : null,
        invoiceNumber:   invoiceNumber || null,
        notes:           notes || null,
        receiptUrl:      JSON.stringify(receiptImages.map(i => i.url)),
        receiptPublicId: JSON.stringify(receiptImages.map(i => i.public_id || null)),
        invoiceId:       currentInvoice.id_invoice || null,
        noOrder:         noOrderMode,
        invoiceDate:     invoiceDate || null,
        silent:          true,
        items: items.map(item => ({
          ...item,
          received_qty:  parseQtyOrZero(item.received_qty),
          price_invoice: item.price_invoice !== '' ? parseFloat(item.price_invoice) : null
        }))
      };
      const res = await saveInvoice(payload);
      if (res.success) {
        if (!currentInvoice.id_invoice && res.data?.id_invoice) {
          setCurrentInvoice(prev => ({ ...prev, id_invoice: res.data.id_invoice }));
        }
        setSavedDraft(true);
        setTimeout(() => setSavedDraft(false), 2500);
        await fetchInvoices();
        await fetchDates();
      }
    } catch (err) {
      alert('Error saving invoice: ' + (err.response?.data?.message || err.message));
    } finally { setSaving(false); }
  };

  // ============================================================
  // SAVE & SEND
  // ============================================================
  const handleSaveAndSend = async () => {
    if (!currentInvoice) return;
    if (noOrderMode && !selectedVendor) { alert('Please select a vendor first.'); return; }
    try {
      setSaving(true);
      const payload = {
        orderId:         currentInvoice.id_order || null,
        storeId:         selectedStore.id,
        vendorId:        noOrderMode ? selectedVendor?.id_vendors : null,
        invoiceNumber:   invoiceNumber || null,
        notes:           notes || null,
        receiptUrl:      JSON.stringify(receiptImages.map(i => i.url)),
        receiptPublicId: JSON.stringify(receiptImages.map(i => i.public_id || null)),
        invoiceId:       currentInvoice.id_invoice || null,
        noOrder:         noOrderMode,
        invoiceDate:     invoiceDate || null,
        items: items.map(item => ({
          ...item,
          received_qty:  parseQtyOrZero(item.received_qty),
          price_invoice: item.price_invoice !== '' ? parseFloat(item.price_invoice) : null
        }))
      };
      const res = await saveInvoice(payload);
      if (res.success) {
        setSaveResult(res.data);
        setShowResultModal(true);
        await fetchInvoices();
        await fetchDates();
      }
    } catch (err) {
      alert('Error saving invoice: ' + (err.response?.data?.message || err.message));
    } finally { setSaving(false); }
  };

  const handleCloseResult = () => {
    setShowResultModal(false); setSaveResult(null);
    setView('list'); setCurrentInvoice(null); setItems([]);
    setReceiptImages([]); setNoOrderMode(false); setSelectedVendor(null);
    setInvoiceDate(todayISO());
    if (selectedDate) handleDateChange(selectedDate);
  };

  // ── Totales ──
  // price_app / price_invoice son siempre precio POR CASE. Si el item se
  // recibió por su container_type (ej. "Bag") en vez de "Case", received_qty
  // viene en unidades sueltas y hay que convertirlo a cases (÷ case_size)
  // antes de multiplicar por el precio — si no, el total sale inflado
  // (ej. 30 bolsitas se cobraban como si fueran 30 cajas).
  const getQtyInCases = (item) => {
    const qty = parseFloat(item.received_qty) || 0;
    if (!item.received_by || item.received_by === 'Case') return qty;
    const caseSize = parseFloat(item.case_size) || 0;
    return caseSize > 0 ? qty / caseSize : qty; // sin case_size no hay forma de convertir
  };

  const totalReceived = items.reduce((sum, item) => {
    const qtyInCases = getQtyInCases(item);
    const price = parseFloat(item.price_invoice) || parseFloat(item.price_app) || 0;
    return sum + qtyInCases * price;
  }, 0);

  const filteredProducts = allProducts.filter(p =>
    p.product_name.toLowerCase().includes(productSearch.toLowerCase()) ||
    (p.product_code || '').toLowerCase().includes(productSearch.toLowerCase())
  );

  // Productos que ya están en el invoice (para no dejar agregarlos dos veces
  // mientras el modal se queda abierto y el usuario sigue navegando la lista).
  const addedProductIds = new Set(items.map(i => i.id_product).filter(Boolean));

  // ============================================================
  // NO STORE
  // ============================================================
  if (!selectedStore) {
    return (
      <div className="inv-page">
        <div className="inv-page-header">
          <h1 className="inv-page-title">Invoices</h1>
        </div>
        <div className="inv-no-store-msg"><p>Please select a store from the sidebar</p></div>
      </div>
    );
  }

  // ============================================================
  // RENDER — FORMULARIO
  // ============================================================
  if (view === 'form') {
    return (
      <div className="inv-page inv-page--form">
        {loadingInvoice && (
          <div className="inv-loading-overlay">
            <span className="inv-spinner">Loading invoice…</span>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileSelected}
        />

        {/* ══════════════════════════════════════════════════
            STICKY HEADER — desktop keeps all buttons inline;
            mobile shows only Back + title + overflow (⋮)
        ══════════════════════════════════════════════════ */}
        <div className="inv-page-header inv-sticky-header">
          <div className="inv-header-left">
            <button className="inv-btn-back" onClick={() => {
              setView('list'); setReceiptImages([]);
              setNoOrderMode(false); setSelectedVendor(null);
            }}>← Back</button>
            <h1 className="inv-page-title">Invoice</h1>
            {noOrderMode && (
              <span className="inv-no-order-badge inv-desktop-only">
                <FaBoxOpen /> No Order
              </span>
            )}
            <span className="inv-store-badge inv-desktop-only">{selectedStore.name}</span>
          </div>

          {/* Desktop buttons — hidden on mobile */}
          <div className="inv-header-buttons inv-desktop-only">
            <button className="inv-btn-add-product-top" onClick={handleOpenProductModal}>
              <FaPlus /> <span>Add Product</span>
            </button>
            <button
              className={`inv-btn-camera ${receiptImages.length ? 'inv-cam-done' : ''}`}
              onClick={receiptImages.length ? () => setShowReceiptModal(true) : handleCameraClick}
              disabled={uploadingReceipt}
              title={receiptImages.length ? 'View receipt photos' : 'Scan receipt'}
            >
              {uploadingReceipt ? (
                <span className="inv-btn-uploading">Uploading…</span>
              ) : receiptImages.length ? (
                <><FaImage /> View Receipt ({receiptImages.length})</>
              ) : (
                <><FaCamera /> Scan Receipt</>
              )}
            </button>
            {receiptImages.length > 0 && (
              <button className="inv-btn-retake" onClick={handleCameraClick} disabled={uploadingReceipt} title="Add more photos">
                <FaCamera />
              </button>
            )}
            <button className="inv-btn-save-draft" onClick={handleSaveDraft} disabled={saving}>
              {saving ? 'Saving…' : savedDraft ? '✓ Saved' : <><FaSave /> Save</>}
            </button>
            <button className="inv-btn-primary" onClick={handleSaveAndSend} disabled={saving}>
              <FaSave /> {saving ? 'Saving…' : 'Save & Send'}
            </button>
          </div>

          {/* Mobile overflow menu trigger — only on mobile */}
          <div className="inv-mobile-header-right inv-mobile-only" ref={mobileMenuRef}>
            {uploadingReceipt && (
              <span className="inv-uploading-badge">Uploading…</span>
            )}
            <button
              className="inv-btn-overflow"
              onClick={() => setShowMobileMenu(v => !v)}
              aria-label="More options"
            >
              <FaEllipsisV />
            </button>

            {showMobileMenu && (
              <div className="inv-overflow-menu">
                <button
                  className="inv-overflow-item"
                  onClick={handleOpenProductModal}
                >
                  <FaPlus /> Add Product
                </button>
                <button
                  className="inv-overflow-item"
                  onClick={receiptImages.length ? () => { setShowMobileMenu(false); setShowReceiptModal(true); } : handleCameraClick}
                  disabled={uploadingReceipt}
                >
                  {receiptImages.length
                    ? <><FaImage /> View Receipt ({receiptImages.length})</>
                    : <><FaCamera /> Scan Receipt</>}
                </button>
                {receiptImages.length > 0 && (
                  <button className="inv-overflow-item" onClick={handleCameraClick} disabled={uploadingReceipt}>
                    <FaCamera /> Add More Photos
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── INFO ORDEN / VENDOR ── */}
        <div className="inv-order-info">
          <div className="inv-info-grid">
            {noOrderMode ? (
              <>
                <div className="inv-info-item">
                  <span className="inv-info-label">Mode</span>
                  <span className="inv-info-value inv-info-noorder">
                    <FaBoxOpen style={{ marginRight: 4 }} /> No Order
                  </span>
                </div>
                <div className="inv-info-item">
                  <span className="inv-info-label">Vendor</span>
                  <span className="inv-info-value">{selectedVendor?.vendor_name || '—'}</span>
                </div>
                <div className="inv-info-item">
                  <span className="inv-info-label">Invoice Date</span>
                  <input
                    type="date"
                    className="inv-inline-input inv-date-input"
                    value={invoiceDate}
                    onChange={e => setInvoiceDate(e.target.value)}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="inv-info-item">
                  <span className="inv-info-label">Order #</span>
                  <span className="inv-info-value">{currentInvoice?.order?.order_number || '—'}</span>
                </div>
                <div className="inv-info-item">
                  <span className="inv-info-label">Vendor</span>
                  <span className="inv-info-value">{currentInvoice?.order?.vendor_name || '—'}</span>
                </div>
                <div className="inv-info-item">
                  <span className="inv-info-label">Order Date</span>
                  <span className="inv-info-value">{formatDate(currentInvoice?.order?.sent_at)}</span>
                </div>
                <div className="inv-info-item">
                  <span className="inv-info-label">Invoice Date</span>
                  <input
                    type="date"
                    className="inv-inline-input inv-date-input"
                    value={invoiceDate}
                    onChange={e => setInvoiceDate(e.target.value)}
                  />
                </div>
              </>
            )}
            <div className="inv-info-item">
              <span className="inv-info-label">Invoice #</span>
              <input type="text" className="inv-inline-input" placeholder="Enter invoice number"
                value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} />
            </div>
          </div>

          {receiptImages.length > 0 && (
            <div className="inv-receipt-thumb" onClick={() => setShowReceiptModal(true)} title="Click to view full size">
              <img src={receiptImages[0].url} alt="Receipt" />
              <span className="inv-receipt-label">
                <FaImage /> {receiptImages.length} photo{receiptImages.length !== 1 ? 's' : ''} attached
              </span>
            </div>
          )}
        </div>

        {/* ── TABLA DESKTOP ── */}
        <div className="inv-form-table-wrap inv-desktop-only">
          <table className="inv-form-table">
            <thead>
              <tr>
                <th>Product Name</th>
                <th>Code</th>
                <th>Size</th>
                {!noOrderMode && <th>Ordered</th>}
                <th>Received</th>
                <th>Received By</th>
                <th>Price in App</th>
                <th>Price in Invoice</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={noOrderMode ? 7 : 9}
                      style={{ textAlign: 'center', padding: '32px', color: '#94a3b8' }}>
                    {noOrderMode
                      ? <span>No products yet. Click <strong>Add Product</strong> to start.</span>
                      : 'No items'}
                  </td>
                </tr>
              )}
              {items.map((item, index) => {
                // ordered_qty siempre está en cases; comparar contra la cantidad
                // convertida (getQtyInCases) evita un falso "warning" cuando el
                // item se recibió por su container_type (ej. bolsitas).
                const hasQtyDiff = item.received_qty !== '' && item.received_qty !== null &&
                  !item.is_extra &&
                  Math.abs(getQtyInCases(item) - item.ordered_qty) > 0.001;
                const hasPriceDiff = item.price_invoice !== '' && item.price_invoice !== null &&
                  Math.abs(parseFloat(item.price_invoice) - item.price_app) > 0.001;
                return (
                  <tr key={index}
                      className={`${item.is_extra ? 'inv-row-extra' : ''} ${hasQtyDiff ? 'inv-row-qty-diff' : ''}`}>
                    <td className="inv-form-cell-name">
                      {!!item.is_extra && <span className="inv-extra-tag">Extra</span>}
                      {item.product_name}
                    </td>
                    <td>{item.product_code || '—'}</td>
                    <td>{item.container_size ? `${item.container_size} ${item.container_unit || ''}` : '—'}</td>
                    {!noOrderMode && <td>{item.ordered_qty ?? 0}</td>}
                    <td>
                      <input type="number" step="1" min="0"
                        className={`inv-cell-input ${hasQtyDiff ? 'inv-warn' : ''}`}
                        value={item.received_qty ?? ''}
                        onChange={e => handleItemChange(index, 'received_qty', e.target.value)}
                        placeholder="0" />
                    </td>
                    <td>
                      <select
                        className="inv-cell-input inv-cell-dropdown"
                        value={item.received_by || ''}
                        onChange={e => handleItemChange(index, 'received_by', e.target.value)}
                      >
                        <option value="">—</option>
                        <option value="Case">Case</option>
                        {item.container_type && item.container_type !== 'Case' && (
                          <option value={item.container_type}>{item.container_type}</option>
                        )}
                      </select>
                    </td>
                    <td>{formatCurrency(item.price_app)}</td>
                    <td>
                      <input type="number" step="1" min="0"
                        className={`inv-cell-input ${hasPriceDiff ? 'inv-price-diff' : ''}`}
                        value={item.price_invoice ?? ''}
                        onChange={e => handleItemChange(index, 'price_invoice', e.target.value)}
                        placeholder={formatCurrency(item.price_app)} />
                    </td>
                    <td>
                      {(!!item.is_extra || noOrderMode) && (
                        <button className="inv-btn-remove-extra" onClick={() => handleRemoveExtra(index)}>
                          <FaTimes />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* ── CARDS MOBILE ── */}
        <div className="inv-mobile-cards inv-mobile-only">
          {items.length === 0 && (
            <div className="inv-empty inv-empty--dashed">
              {noOrderMode
                ? 'No products yet. Tap Add Product below to start.'
                : 'No items in this invoice.'}
            </div>
          )}
          {items.map((item, index) => {
            const hasQtyDiff = item.received_qty !== '' && item.received_qty !== null &&
              !item.is_extra &&
              Math.abs(getQtyInCases(item) - item.ordered_qty) > 0.001;
            const hasPriceDiff = item.price_invoice !== '' && item.price_invoice !== null &&
              Math.abs(parseFloat(item.price_invoice) - item.price_app) > 0.001;
            return (
              <div key={index}
                   className={`inv-mobile-card ${item.is_extra ? 'inv-card-extra' : ''} ${hasQtyDiff ? 'inv-card-qty-diff' : ''}`}>
                <div className="inv-mobile-card-header">
                  <div>
                    {!!item.is_extra && <span className="inv-extra-tag">Extra</span>}
                    <div className="inv-mobile-name">{item.product_name}</div>
                    <div className="inv-mobile-code">
                      {item.product_code || '—'} · {item.container_size ? `${item.container_size} ${item.container_unit || ''}` : '—'}
                    </div>
                  </div>
                  {(!!item.is_extra || noOrderMode) && (
                    <button className="inv-btn-remove-extra" onClick={() => handleRemoveExtra(index)}>
                      <FaTimes />
                    </button>
                  )}
                </div>
                <div className="inv-mobile-fields">
                  {!noOrderMode && (
                    <div className="inv-mobile-field">
                      <span className="inv-mobile-label">Ordered</span>
                      <span className="inv-mobile-value">{item.ordered_qty ?? 0}</span>
                    </div>
                  )}
                  <div className="inv-mobile-field">
                    <span className="inv-mobile-label">Received</span>
                    <input type="number" step="1" min="0"
                      className={`inv-mobile-input ${hasQtyDiff ? 'inv-warn' : ''}`}
                      value={item.received_qty ?? ''}
                      onChange={e => handleItemChange(index, 'received_qty', e.target.value)}
                      placeholder="0" />
                  </div>
                  <div className="inv-mobile-field">
                    <span className="inv-mobile-label">Received By</span>
                    <select
                      className="inv-mobile-input"
                      value={item.received_by || ''}
                      onChange={e => handleItemChange(index, 'received_by', e.target.value)}
                    >
                      <option value="">Select…</option>
                      <option value="Case">Case</option>
                      {item.container_type && item.container_type !== 'Case' && (
                        <option value={item.container_type}>{item.container_type}</option>
                      )}
                    </select>
                  </div>
                  <div className="inv-mobile-field">
                    <span className="inv-mobile-label">Price App</span>
                    <span className="inv-mobile-value">{formatCurrency(item.price_app)}</span>
                  </div>
                  <div className="inv-mobile-field">
                    <span className="inv-mobile-label">Price Invoice</span>
                    <input type="number" step="1" min="0"
                      className={`inv-mobile-input ${hasPriceDiff ? 'inv-price-diff' : ''}`}
                      value={item.price_invoice ?? ''}
                      onChange={e => handleItemChange(index, 'price_invoice', e.target.value)}
                      placeholder={item.price_app ? `$${item.price_app}` : '0'} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── DESKTOP FOOTER ── */}
        <div className="inv-form-footer inv-desktop-only">
          <button className="inv-btn-add-product" onClick={handleOpenProductModal}>
            <FaPlus /> Add Product
          </button>
          <div className="inv-total-strip">
            <span className="inv-total-label">Total:</span>
            <span className="inv-total-value">{formatCurrency(totalReceived)}</span>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════
            MOBILE BOTTOM ACTION BAR
        ══════════════════════════════════════════════════ */}
        <div className="inv-mobile-action-bar inv-mobile-only">
          <div className="inv-mobile-total">
            <span className="inv-mobile-total-label">Total</span>
            <span className="inv-mobile-total-value">{formatCurrency(totalReceived)}</span>
          </div>
          <div className="inv-mobile-action-buttons">
            <button
              className="inv-mobile-action-btn inv-mobile-action-btn--ghost"
              onClick={handleSaveDraft}
              disabled={saving}
            >
              {saving ? '…' : savedDraft ? '✓' : <FaSave />}
              <span>{savedDraft ? 'Saved' : 'Draft'}</span>
            </button>
            <button
              className="inv-mobile-action-btn inv-mobile-action-btn--secondary"
              onClick={handleOpenProductModal}
            >
              <FaPlus />
              <span>Add Product</span>
            </button>
            <button
              className="inv-mobile-action-btn inv-mobile-action-btn--primary"
              onClick={handleSaveAndSend}
              disabled={saving}
            >
              <FaSave />
              <span>{saving ? 'Saving…' : 'Save & Send'}</span>
            </button>
          </div>
        </div>

        {/* ── MODAL GALERÍA DE RECIBOS ── */}
        {showReceiptModal && receiptImages.length > 0 && (
          <div className="inv-modal-overlay">
            <div className="inv-receipt-modal" onClick={e => e.stopPropagation()}>
              <div className="inv-modal-header">
                <h2><FaImage /> Receipt ({receiptImages.length})</h2>
                <button className="inv-btn-close" onClick={() => setShowReceiptModal(false)}><FaTimes /></button>
              </div>
              <div className="inv-receipt-gallery">
                {receiptImages.map((img, idx) => (
                  <div key={idx} className="inv-receipt-gallery-item">
                    <img src={img.url} alt={`Receipt ${idx + 1}`} />
                    <button
                      className="inv-receipt-remove-btn"
                      onClick={() => handleRemoveReceiptImage(idx)}
                      disabled={removingImageIndex === idx}
                      title="Remove this photo"
                    >
                      {removingImageIndex === idx ? '…' : <FaTrash />}
                    </button>
                    <a href={img.url} target="_blank" rel="noopener noreferrer" className="inv-receipt-gallery-open">
                      Open full size
                    </a>
                  </div>
                ))}
              </div>
              <div className="inv-modal-footer">
                <button className="inv-btn-secondary" onClick={handleCameraClick} disabled={uploadingReceipt}>
                  <FaCamera /> {uploadingReceipt ? 'Uploading…' : 'Add More Photos'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── MODAL ADD PRODUCT ── */}
        {showProductModal && (
          <div className="inv-modal-overlay">
            <div className="inv-modal-content inv-modal-large" onClick={e => e.stopPropagation()}>
              <div className="inv-modal-header">
                <h2>Add Product</h2>
                <button className="inv-btn-close" onClick={() => setShowProductModal(false)}><FaTimes /></button>
              </div>
              <div className="inv-modal-body">
                <div className="inv-form-group">
                  <input type="text" placeholder="Search product…" value={productSearch}
                    onChange={e => setProductSearch(e.target.value)} autoFocus />
                </div>
                <div className="inv-picker-table-wrap">
                  {loadingProducts && <div className="inv-empty">Loading…</div>}
                  {filteredProducts.length === 0 && !loadingProducts && (
                    <div className="inv-empty">No products found.</div>
                  )}
                  {/* Desktop table */}
                  {filteredProducts.length > 0 && (
                    <table className="inv-picker-table inv-desktop-only">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Size</th>
                          <th>Price in App</th>
                          <th>Received</th>
                          <th>Received By</th>
                          <th>Price Invoice</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredProducts.map(p => {
                          const fv = productFormValues[p.id_products] || {};
                          const hasPriceDiff = fv.price_invoice !== undefined &&
                            fv.price_invoice !== '' &&
                            Math.abs(parseFloat(fv.price_invoice) - parseFloat(p.wholesale_price)) > 0.001;
                          const alreadyAdded = addedProductIds.has(p.id_products);
                          return (
                            <tr key={p.id_products} className={`inv-picker-row ${alreadyAdded ? 'inv-picker-row-added' : ''}`}>
                              <td>
                                <div className="inv-picker-name">{p.product_name}</div>
                                <div className="inv-picker-code">{p.product_code || '—'}</div>
                              </td>
                              <td className="inv-picker-size">
                                {p.container_size} {p.container_unit}
                              </td>
                              <td className="inv-picker-price-app">
                                {formatCurrency(p.wholesale_price)}
                              </td>
                              <td>
                                <input
                                  type="number" step="1" min="0"
                                  className="inv-picker-input"
                                  value={fv.received_qty || ''}
                                  onChange={e => handleProductFormChange(p.id_products, 'received_qty', e.target.value)}
                                  placeholder="0"
                                  onClick={e => e.stopPropagation()}
                                  disabled={alreadyAdded}
                                />
                              </td>
                              <td>
                                <select
                                  className="inv-picker-input"
                                  value={fv.received_by || ''}
                                  onChange={e => handleProductFormChange(p.id_products, 'received_by', e.target.value)}
                                  onClick={e => e.stopPropagation()}
                                  disabled={alreadyAdded}
                                >
                                  <option value="">—</option>
                                  <option value="Case">Case</option>
                                  {p.container_type && p.container_type !== 'Case' && (
                                    <option value={p.container_type}>{p.container_type}</option>
                                  )}
                                </select>
                              </td>
                              <td>
                                <input
                                  type="number" step="1" min="0"
                                  className={`inv-picker-input ${hasPriceDiff ? 'inv-price-diff' : ''}`}
                                  value={fv.price_invoice || ''}
                                  onChange={e => handleProductFormChange(p.id_products, 'price_invoice', e.target.value)}
                                  placeholder={formatCurrency(p.wholesale_price)}
                                  onClick={e => e.stopPropagation()}
                                  disabled={alreadyAdded}
                                />
                              </td>
                              <td>
                                <button
                                  className={`inv-picker-add-btn ${alreadyAdded ? 'inv-picker-add-btn--added' : ''}`}
                                  onClick={() => handleAddProduct(p)}
                                  disabled={alreadyAdded}
                                >
                                  {alreadyAdded ? <><FaCheckCircle /> Added</> : <><FaPlus /> Add</>}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}

                  {/* Mobile cards for product picker */}
                  {filteredProducts.length > 0 && (
                    <div className="inv-picker-mobile-list inv-mobile-only">
                      {filteredProducts.map(p => {
                        const fv = productFormValues[p.id_products] || {};
                        const hasPriceDiff = fv.price_invoice !== undefined &&
                          fv.price_invoice !== '' &&
                          Math.abs(parseFloat(fv.price_invoice) - parseFloat(p.wholesale_price)) > 0.001;
                        const alreadyAdded = addedProductIds.has(p.id_products);
                        return (
                          <div key={p.id_products} className={`inv-picker-mobile-card ${alreadyAdded ? 'inv-picker-row-added' : ''}`}>
                            <div className="inv-picker-mobile-top">
                              <div>
                                <div className="inv-picker-name">{p.product_name}</div>
                                <div className="inv-picker-code">
                                  {p.product_code || '—'} · {p.container_size} {p.container_unit}
                                </div>
                              </div>
                              <div className="inv-picker-price-app">{formatCurrency(p.wholesale_price)}</div>
                            </div>
                            <div className="inv-picker-mobile-inputs">
                              <div className="inv-picker-mobile-field">
                                <label className="inv-picker-mobile-label">Received qty</label>
                                <input
                                  type="number" step="1" min="0"
                                  className="inv-picker-input"
                                  value={fv.received_qty || ''}
                                  onChange={e => handleProductFormChange(p.id_products, 'received_qty', e.target.value)}
                                  placeholder="0"
                                  disabled={alreadyAdded}
                                />
                              </div>
                              <div className="inv-picker-mobile-field">
                                <label className="inv-picker-mobile-label">Received By</label>
                                <select
                                  className="inv-picker-input"
                                  value={fv.received_by || ''}
                                  onChange={e => handleProductFormChange(p.id_products, 'received_by', e.target.value)}
                                  disabled={alreadyAdded}
                                >
                                  <option value="">Select…</option>
                                  <option value="Case">Case</option>
                                  {p.container_type && p.container_type !== 'Case' && (
                                    <option value={p.container_type}>{p.container_type}</option>
                                  )}
                                </select>
                              </div>
                              <div className="inv-picker-mobile-field">
                                <label className="inv-picker-mobile-label">Price invoice</label>
                                <input
                                  type="number" step="1" min="0"
                                  className={`inv-picker-input ${hasPriceDiff ? 'inv-price-diff' : ''}`}
                                  value={fv.price_invoice || ''}
                                  onChange={e => handleProductFormChange(p.id_products, 'price_invoice', e.target.value)}
                                  placeholder={formatCurrency(p.wholesale_price)}
                                  disabled={alreadyAdded}
                                />
                              </div>
                              <button
                                className={`inv-picker-add-btn inv-picker-add-btn--full ${alreadyAdded ? 'inv-picker-add-btn--added' : ''}`}
                                onClick={() => handleAddProduct(p)}
                                disabled={alreadyAdded}
                              >
                                {alreadyAdded ? <><FaCheckCircle /> Added</> : <><FaPlus /> Add</>}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── MODAL RESULTADO ── */}
        {showResultModal && saveResult && (
          <div className="inv-modal-overlay">
            <div className="inv-modal-content inv-modal-medium" onClick={e => e.stopPropagation()}>
              <div className="inv-modal-header">
                <h2>Invoice Saved</h2>
              </div>
              <div className="inv-modal-body">
                <div className="inv-result-success">
                  <FaCheckCircle className="inv-result-icon-ok" />
                  <p>Invoice saved successfully. Total: <strong>{formatCurrency(saveResult.total_amount)}</strong></p>
                </div>
                {receiptImages.length > 0 && (
                  <div className="inv-result-receipt">
                    <FaImage className="inv-receipt-icon" />
                    {receiptImages.length} receipt photo{receiptImages.length !== 1 ? 's' : ''} attached
                  </div>
                )}
                {saveResult.price_discrepancies?.length > 0 && (
                  <div className="inv-result-section">
                    <div className="inv-result-title">
                      <FaExclamationTriangle className="inv-warn-icon" /> Price Updated
                    </div>
                    <p className="inv-result-hint">The following product prices were updated in the system:</p>
                    <table className="inv-result-table">
                      <thead><tr><th>Product</th><th>Old Price</th><th>New Price</th></tr></thead>
                      <tbody>
                        {saveResult.price_discrepancies.map((d, i) => (
                          <tr key={i}>
                            <td>{d.product_name}</td>
                            <td>{formatCurrency(d.price_app)}</td>
                            <td className="inv-new-price">{formatCurrency(d.price_invoice)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {saveResult.quantity_discrepancies?.length > 0 && (
                  <div className="inv-result-section">
                    <div className="inv-result-title">
                      <FaExclamationTriangle className="inv-warn-icon" /> Quantity Discrepancies
                    </div>
                    {saveResult.notification_sent && (
                      <p className="inv-result-hint inv-notif-sent">✓ Notification email sent to recipients.</p>
                    )}
                    <table className="inv-result-table">
                      <thead><tr><th>Product</th><th>Ordered</th><th>Received</th><th>Diff</th></tr></thead>
                      <tbody>
                        {saveResult.quantity_discrepancies.map((d, i) => (
                          <tr key={i}>
                            <td>{d.product_name}</td>
                            <td>{d.ordered_qty}</td>
                            <td>{d.received_qty}</td>
                            <td className={d.difference < 0 ? 'inv-diff-neg' : 'inv-diff-pos'}>
                              {d.difference > 0 ? '+' : ''}{d.difference}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="inv-modal-footer">
                <button className="inv-btn-primary" onClick={handleCloseResult}>Done</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ============================================================
  // RENDER — LISTA
  // ============================================================
  const sortedInvoices = [...invoicesList].sort((a, b) => {
    if (sortBy === 'date_asc')   return new Date(a.display_date) - new Date(b.display_date);
    if (sortBy === 'vendor')     return (a.vendor_name || '').localeCompare(b.vendor_name || '');
    if (sortBy === 'total_desc') return parseFloat(b.total_amount || 0) - parseFloat(a.total_amount || 0);
    return new Date(b.display_date) - new Date(a.display_date);
  });

  const totalInvoices = sortedInvoices.length;
  const savedInvoices = sortedInvoices.filter(i => i.status === 'Saved').length;
  const totalValue    = sortedInvoices.reduce((s, i) => s + parseFloat(i.total_amount || 0), 0);

  return (
    <div className="inv-page">
      <div className="inv-page-header inv-sticky-header">
        <div className="inv-header-left">
          <h1 className="inv-page-title">Invoices</h1>
          <span className="inv-store-badge">{selectedStore.name}</span>
        </div>
        <div className="inv-header-buttons">
          <button
            className="inv-btn-no-order"
            onClick={handleOpenNoOrderFlow}
            disabled={loadingVendors}
            title="Create an invoice without an existing order"
          >
            <FaBoxOpen />
            {loadingVendors ? 'Loading…' : 'Receive Invoice (No Orders)'}
          </button>
        </div>
      </div>

      {error && (
        <div className="inv-error-msg">
          {error}
          <button onClick={() => { fetchDates(); fetchInvoices(); }}>Retry</button>
        </div>
      )}

      {invoicesList.length > 0 && (
        <div className="inv-stats-strip">
          <div className="inv-stat-card">
            <div className="inv-stat-value">{totalInvoices}</div>
            <div className="inv-stat-label">Total Invoices</div>
          </div>
          <div className="inv-stat-card">
            <div className={`inv-stat-value inv-stat-value-green`}>{savedInvoices}</div>
            <div className="inv-stat-label">Saved</div>
          </div>
          <div className="inv-stat-card">
            <div className="inv-stat-value">{formatCurrency(totalValue)}</div>
            <div className="inv-stat-label">Total Value</div>
          </div>
        </div>
      )}

      <div className="inv-selectors">
        <div className="inv-selector-group">
          <label className="inv-selector-label">Select order date</label>
          <select className="inv-select" value={selectedDate}
            onChange={e => handleDateChange(e.target.value)} disabled={loadingDates}>
            <option value="">— Select a date —</option>
            {availableDates.map(d => (
              <option key={d.date} value={d.date}>
                {formatDate(d.date)} ({d.order_count} order{d.order_count !== 1 ? 's' : ''})
              </option>
            ))}
          </select>
          {loadingDates && <span className="inv-selector-hint">Loading dates…</span>}
          {!loadingDates && availableDates.length === 0 && (
            <span className="inv-selector-hint">No sent orders found for this store.</span>
          )}
        </div>
      </div>

      {selectedDate && (
        <div className="inv-orders-section">
          <div className="inv-section-title">Orders — {formatDate(selectedDate)}</div>
          {loadingOrders && <div className="inv-empty" style={{ padding: '20px' }}>Loading orders…</div>}
          {!loadingOrders && ordersForDate.length === 0 && (
            <div className="inv-empty" style={{ padding: '24px' }}>No orders found for this date.</div>
          )}
          {!loadingOrders && ordersForDate.map(order => (
            <div key={order.id_orders} className="inv-order-card">
              <div className="inv-order-card-left">
                <div className="inv-order-card-number">{order.order_number}</div>
                <div className="inv-order-card-vendor">{order.vendor_name}</div>
                <div className="inv-order-card-meta">
                  {order.total_items} items · {formatCurrency(order.total_amount)}
                </div>
              </div>
              <div className="inv-order-card-right">
                {order.id_invoice ? (
                  <span className={`inv-invoice-badge inv-badge-${(order.invoice_status || '').toLowerCase()}`}>
                    Invoice: {order.invoice_status}
                  </span>
                ) : (
                  <span className="inv-invoice-badge inv-badge-none">No invoice</span>
                )}
                <button className="inv-btn-primary inv-btn-sm" onClick={() => handleOpenOrder(order)}>
                  <FaFileInvoiceDollar />
                  {order.id_invoice ? ' Open Invoice' : ' Receive Invoice'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── SAVED INVOICES ── */}
      <div className="inv-saved-section">
        <div className="inv-saved-header">
          <div className="inv-section-title">Saved Invoices</div>
          <div className="inv-sort-control">
            <span className="inv-sort-label">Order by:</span>
            <select className="inv-sort-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
              <option value="date_desc">Newest first</option>
              <option value="date_asc">Oldest first</option>
              <option value="vendor">Vendor A–Z</option>
              <option value="total_desc">Highest total</option>
            </select>
          </div>
        </div>

        {loadingList && <div className="inv-empty" style={{ padding: '20px' }}>Loading…</div>}
        {!loadingList && sortedInvoices.length === 0 && (
          <div className="inv-empty" style={{ padding: '24px' }}>No invoices saved yet.</div>
        )}

        {/* ── DESKTOP TABLE ── */}
        {!loadingList && sortedInvoices.length > 0 && (
          <div className="inv-desktop-only">
            <div className="inv-saved-table-wrap">
              <table className="inv-saved-table">
                <colgroup>
                  <col className="col-order" />
                  <col className="col-invoice" />
                  <col className="col-vendor" />
                  <col className="col-date" />
                  <col className="col-items" />
                  <col className="col-total" />
                  <col className="col-receipt" />
                  <col className="col-status" />
                  <col className="col-actions" />
                </colgroup>
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>Invoice #</th>
                    <th>Vendor</th>
                    <th>Date</th>
                    <th style={{ textAlign: 'center' }}>Items</th>
                    <th>Total</th>
                    <th>Receipt</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedInvoices.map(inv => {
                    const invReceiptImages = parseReceiptImages(inv.receipt_url, null);
                    return (
                      <tr key={inv.id_invoice}>
                        <td>
                          {inv.order_number
                            ? <span className="inv-order-mono">{inv.order_number}</span>
                            : <span className="inv-no-order-tag"><FaBoxOpen /> No Order</span>
                          }
                        </td>
                        <td>{inv.invoice_number || '—'}</td>
                        <td className="inv-vendor-cell inv-cell-wrap">{inv.vendor_name}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDate(inv.display_date)}</td>
                        <td className="inv-cell-center">{inv.total_items}</td>
                        <td className="inv-cell-total">{formatCurrency(inv.total_amount)}</td>
                        <td>
                          {invReceiptImages.length > 0
                            ? <a href={invReceiptImages[0].url} target="_blank" rel="noopener noreferrer"
                                 className="inv-receipt-link">
                                <FaImage /> View{invReceiptImages.length > 1 ? ` (${invReceiptImages.length})` : ''}
                              </a>
                            : <span className="inv-no-receipt">—</span>
                          }
                        </td>
                        <td>
                          <span className={`inv-status-dot ${(inv.status || '').toLowerCase()}`}>
                            <span className="inv-dot" />
                            {inv.status}
                          </span>
                        </td>
                        <td>
                          <div className="inv-row-actions">
                            <button className="inv-view-btn" onClick={() => handleOpenExisting(inv)}>
                              View
                            </button>
                            <button
                              className="inv-delete-btn"
                              onClick={(e) => handleDeleteClick(inv, e)}
                              disabled={deletingId === inv.id_invoice}
                              title="Delete invoice"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="inv-table-footer">
                Showing {sortedInvoices.length} invoice{sortedInvoices.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
        )}

        {/* ── MOBILE CARDS ── */}
        {!loadingList && sortedInvoices.length > 0 && (
          <div className="inv-list-cards inv-mobile-only">
            {sortedInvoices.map(inv => {
              const invReceiptImages = parseReceiptImages(inv.receipt_url, null);
              return (
                <div key={inv.id_invoice} className="inv-list-card">
                  <div className="inv-list-card-top">
                    {inv.order_number
                      ? <span className="inv-list-order-badge">{inv.order_number}</span>
                      : <span className="inv-list-order-badge inv-list-no-order-badge"><FaBoxOpen /> No Order</span>
                    }
                    <span className={`inv-status-dot ${(inv.status || '').toLowerCase()}`}>
                      <span className="inv-dot" />{inv.status}
                    </span>
                  </div>
                  <div className="inv-list-card-vendor">
                    <div className="inv-list-vendor-name">{inv.vendor_name}</div>
                    <div className="inv-list-date">{formatDate(inv.display_date)}</div>
                  </div>
                  <div className="inv-list-card-stats">
                    <div className="inv-list-stat">
                      <span className="inv-list-stat-label">Invoice #</span>
                      <span className="inv-list-stat-value">{inv.invoice_number || '—'}</span>
                    </div>
                    <div className="inv-list-stat">
                      <span className="inv-list-stat-label">Items</span>
                      <span className="inv-list-stat-value">{inv.total_items}</span>
                    </div>
                    <div className="inv-list-stat">
                      <span className="inv-list-stat-label">Total</span>
                      <span className="inv-list-stat-value inv-list-stat-total">{formatCurrency(inv.total_amount)}</span>
                    </div>
                  </div>
                  <div className="inv-list-card-footer">
                    {invReceiptImages.length > 0
                      ? <a href={invReceiptImages[0].url} target="_blank" rel="noopener noreferrer"
                           className="inv-receipt-link">
                          <FaImage /> Receipt{invReceiptImages.length > 1 ? ` (${invReceiptImages.length})` : ''}
                        </a>
                      : <span />
                    }
                    <div className="inv-list-card-footer-actions">
                      <button
                        className="inv-list-delete-btn"
                        onClick={(e) => handleDeleteClick(inv, e)}
                        disabled={deletingId === inv.id_invoice}
                        title="Delete invoice"
                      >
                        <FaTrash />
                      </button>
                      <button className="inv-list-view-btn" onClick={() => handleOpenExisting(inv)}>
                        View Invoice
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── MODAL CONFIRMAR DELETE ── */}
      {showDeleteModal && invoiceToDelete && (
        <div className="inv-modal-overlay">
          <div className="inv-modal-content inv-modal-small" onClick={e => e.stopPropagation()}>
            <div className="inv-modal-header">
              <h2>Delete Invoice</h2>
              <button className="inv-btn-close" onClick={() => setShowDeleteModal(false)}><FaTimes /></button>
            </div>
            <div className="inv-modal-body">
              <div className="inv-delete-confirm">
                <div className="inv-delete-icon"><FaTrash /></div>
                <p>Are you sure you want to delete this invoice?</p>
                <div className="inv-delete-detail">
                  <span className="inv-delete-vendor">{invoiceToDelete.vendor_name}</span>
                  {invoiceToDelete.order_number && (
                    <span className="inv-delete-order">{invoiceToDelete.order_number}</span>
                  )}
                  <span className="inv-delete-total">{formatCurrency(invoiceToDelete.total_amount)}</span>
                </div>
                {!invoiceToDelete.no_order && (
                  <p className="inv-delete-warning">
                    ⚠️ The associated order will be returned to <strong>Sent</strong> status.
                  </p>
                )}
              </div>
            </div>
            <div className="inv-modal-footer">
              <button className="inv-btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
              <button className="inv-btn-danger" onClick={handleConfirmDelete} disabled={!!deletingId}>
                <FaTrash /> {deletingId ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL SELECT VENDOR ── */}
      {showVendorModal && (
        <div className="inv-modal-overlay">
          <div className="inv-modal-content inv-modal-medium" onClick={e => e.stopPropagation()}>
            <div className="inv-modal-header">
              <div className="inv-modal-header-inner">
                <FaTruck className="inv-modal-header-icon" />
                <div>
                  <h2>Select Vendor</h2>
                  <p className="inv-modal-header-sub">Create an invoice without an existing order</p>
                </div>
              </div>
              <button className="inv-btn-close" onClick={() => setShowVendorModal(false)}><FaTimes /></button>
            </div>
            <div className="inv-modal-body">
              <div className="inv-form-group">
                <input type="text" placeholder="Search vendor…" value={vendorSearch}
                  onChange={e => setVendorSearch(e.target.value)} autoFocus />
              </div>
              <div className="inv-picker-list">
                {loadingVendors && <div className="inv-empty">Loading vendors…</div>}
                {!loadingVendors && filteredVendors.length === 0 && (
                  <div className="inv-empty">No vendors found.</div>
                )}
                {filteredVendors.map(v => (
                  <div key={v.id_vendors} className="inv-vendor-row" onClick={() => handleSelectVendor(v)}>
                    <div className="inv-vendor-row-icon"><FaTruck /></div>
                    <div>
                      <div className="inv-vendor-name">{v.vendor_name}</div>
                      {v.contact_name && <div className="inv-vendor-meta">{v.contact_name}</div>}
                    </div>
                    <span className="inv-vendor-arrow">→</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Invoices;